// LayoutEditorSlotShell.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './LayoutEditorSlotShell.css';

/**
 * LayoutEditorSlotShell
 *
 * Renders the draggable slot shell, preview wrapper, and interaction overlay around slot content.
 *
 * @param {ReactNode} children - Preview content rendered inside the slot.
 * @param {boolean} isDragging - Whether this slot is currently being dragged.
 * @param {boolean} isDropActive - Whether this slot is the active drop target.
 * @param {boolean} isOverlayHidden - Whether the interaction overlay should be hidden.
 * @param {boolean} isPreviewInteractive - Whether the preview surface should receive pointer events.
 * @param {boolean} isSlotActive - Whether this slot is the current toolbar target.
 * @param {boolean} isSlotHovered - Whether this slot is currently hovered.
 * @param {function} onClick - Click handler for opening slot toolbar controls.
 * @param {function} onDoubleClick - Double-click handler for opening the primary block action.
 * @param {function} onDragEnd - Drag end handler.
 * @param {function} onDragOver - Drag over handler.
 * @param {function} onDragStart - Drag start handler.
 * @param {function} onDrop - Drop handler.
 * @param {function} onMouseEnter - Mouse enter handler.
 * @param {function} onMouseLeave - Mouse leave handler.
 *
 * @returns {ReactNode} The rendered slot shell.
 */
const LayoutEditorSlotShell = ({
	children,
	isDragging = false,
	isDropActive = false,
	isOverlayHidden = false,
	isPreviewInteractive = false,
	isSlotActive = false,
	isSlotHovered = false,
	onClick,
	onDoubleClick,
	onDragEnd,
	onDragOver,
	onDragStart,
	onDrop,
	onMouseEnter,
	onMouseLeave,
}) => {

	// ----- CONTEXT ----- //

	const { theme } = useTheme();
	// ----- RENDER ----- //

	return (
		<div
			className="layout-editor-slot-shell-container"
			style={{
				borderColor: isSlotHovered || isSlotActive ? theme.outline : theme.outlineMuted,
				opacity: isDragging ? 0.45 : 1,
			}}
		>

			{/* LAYOUT EDITOR SLOT SHELL PREVIEW INTERACTIVE */}
			<div
				className={[
					'layout-editor-slot-shell-preview',
					isPreviewInteractive ? 'layout-editor-slot-shell-preview-interactive' : '',
				].filter(Boolean).join(' ')}
			>
				{children}
			</div>

			{/* OVERLAY */}
			{!isOverlayHidden ? (
				<div
					className="layout-editor-slot-shell-overlay"
					style={{
						backgroundColor: isDropActive ? theme.backgroundHover : 'transparent',
						cursor: isDragging ? 'grabbing' : 'grab',
					}}
					draggable
					onClick={onClick}
					onDoubleClick={onDoubleClick}
					onDragEnd={onDragEnd}
					onDragOver={onDragOver}
					onDragStart={onDragStart}
					onDrop={onDrop}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
				/>
			) : null}
		</div>
	);
};

// Dev Tools
LayoutEditorSlotShell.displayName = 'LayoutEditorSlotShell';

export default LayoutEditorSlotShell;
